import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  Clock, 
  Edit, 
  Mail, 
  Tag, 
  Loader2,
  Check,
  X,
  Trash2,
  Send,
  MessageSquare,
} from 'lucide-react';

import { Task, TaskEmailAssociation, TaskComment, getTaskEmails, updateTask, getTaskComments, addTaskComment, deleteTaskComment } from '../../services/taskService';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { useToast } from '../ui/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { MultiSelect } from '../ui/multi-select';
import axiosInstance from '../../lib/api/axiosInstance';

interface TaskDescriptionProps {
  task: Task;
  onEdit?: () => void;
  onDelete?: () => void;
  refreshTask?: () => void;
}

export const TaskDescription: React.FC<TaskDescriptionProps> = ({ 
  task, 
  onEdit,
  onDelete,
  refreshTask
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [emails, setEmails] = useState<TaskEmailAssociation[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [editingDescription, setEditingDescription] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(task.description || '');
  const [newComment, setNewComment] = useState<string>('');
  const [savingDescription, setSavingDescription] = useState<boolean>(false);
  const [addingComment, setAddingComment] = useState<boolean>(false);
  
  // Nouvel état pour la gestion de l'attribution
  const [editingAssignment, setEditingAssignment] = useState<boolean>(false);
  const [assignedUsers, setAssignedUsers] = useState<number[]>(
    task.assignedToId ? [task.assignedToId] : []
  );
  const [savingAssignment, setSavingAssignment] = useState<boolean>(false);
  const [users, setUsers] = useState<{ id: number; name: string; email: string; role?: string }[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const taskEmails = await getTaskEmails(task.id);
        setEmails(taskEmails);
      } catch (error) {
        console.error('Error fetching task emails:', error);
        toast({
          variant: 'destructive',
          title: t('tasks.emailsFetchError', 'Erreur'),
          description: t('tasks.emailsFetchErrorDesc', 'Impossible de récupérer les emails associés à cette tâche'),
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchComments = async () => {
      setLoadingComments(true);
      try {
        const taskComments = await getTaskComments(task.id);
        setComments(taskComments);
      } catch (error) {
        console.error('Error fetching task comments:', error);
        toast({
          variant: 'destructive',
          title: t('tasks.commentsFetchError', 'Erreur'),
          description: t('tasks.commentsFetchErrorDesc', 'Impossible de récupérer les commentaires de cette tâche'),
        });
      } finally {
        setLoadingComments(false);
      }
    };

    // Fonction pour récupérer les utilisateurs pour l'attribution
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get<{ id: number; name: string; email: string; role?: string }[]>('/users');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          variant: 'destructive',
          title: t('tasks.usersFetchError', 'Erreur'),
          description: t('tasks.usersFetchErrorDesc', 'Impossible de récupérer la liste des utilisateurs'),
        });
      } 
    };

    fetchData();
    fetchComments();
    fetchUsers(); // Charger les utilisateurs pour l'attribution
  }, [task.id, t, toast]);

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return t('common.notSpecified', 'Non spécifié');
    return format(new Date(dateString), 'PPP', { locale: fr });
  };

  const formatTime = (dateString: string | undefined | null) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'HH:mm', { locale: fr });
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      console.log("Changing status, task data:", {
        taskId: task.id,
        currentStatus: task.status,
        newStatus: newStatus,
        fullTask: task
      });
      
      // Vérifier le format des données envoyées
      const updateData = { status: newStatus as 'TODO' | 'IN_PROGRESS' | 'DONE' };
      console.log("Data being sent to API:", updateData);
      
      await updateTask(task.id, { status: newStatus as 'TODO' | 'IN_PROGRESS' | 'DONE' });
      
      toast({
        title: t('tasks.statusUpdated', 'Statut mis à jour'),
        description: t('tasks.statusUpdatedDesc', 'Le statut de la tâche a été mis à jour avec succès'),
      });

      if (refreshTask) {
        refreshTask();
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      if (error.response) {
        console.error('Response error data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request made but no response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      console.error('Error config:', error.config);
      
      toast({
        variant: 'destructive',
        title: t('tasks.statusUpdateError', 'Erreur'),
        description: t('tasks.statusUpdateErrorDesc', 'Impossible de mettre à jour le statut de la tâche'),
      });
    }
  };

  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      console.log("Saving description, task data:", {
        taskId: task.id,
        description: description,
        fullTask: task
      });
      
      // Vérifier le format des données envoyées
      const updateData = { description };
      console.log("Data being sent to API:", updateData);
      
      await updateTask(task.id, { description });
      setEditingDescription(false);
      
      toast({
        title: t('tasks.descUpdated', 'Description mise à jour'),
        description: t('tasks.descUpdatedDesc', 'La description de la tâche a été mise à jour avec succès'),
      });

      if (refreshTask) {
        refreshTask();
      }
    } catch (error: any) {
      console.error('Error updating task description:', error);
      if (error.response) {
        console.error('Response error data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request made but no response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      console.error('Error config:', error.config);
      
      toast({
        variant: 'destructive',
        title: t('tasks.descUpdateError', 'Erreur'),
        description: t('tasks.descUpdateErrorDesc', 'Impossible de mettre à jour la description de la tâche'),
      });
    } finally {
      setSavingDescription(false);
    }
  };

  // Nouvelle fonction pour sauvegarder les attributions
  const handleSaveAssignment = async () => {
    setSavingAssignment(true);
    try {
      // Pour l'instant, on gère un seul utilisateur assigné (comme dans le modèle actuel)
      // Dans une future mise à jour, nous pourrions modifier le modèle pour gérer plusieurs utilisateurs
      const assignedToId = assignedUsers.length > 0 ? assignedUsers[0] : null;
      
      console.log("Saving assignment, task data:", {
        taskId: task.id,
        assignedToId: assignedToId,
        fullTask: task
      });
      
      await updateTask(task.id, { assignedToId });
      setEditingAssignment(false);
      
      toast({
        title: t('tasks.assignmentUpdated', 'Attribution mise à jour'),
        description: t('tasks.assignmentUpdatedDesc', 'L\'attribution de la tâche a été mise à jour avec succès'),
      });

      if (refreshTask) {
        refreshTask();
      }
    } catch (error: any) {
      console.error('Error updating task assignment:', error);
      if (error.response) {
        console.error('Response error data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request made but no response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      
      toast({
        variant: 'destructive',
        title: t('tasks.assignmentUpdateError', 'Erreur'),
        description: t('tasks.assignmentUpdateErrorDesc', 'Impossible de mettre à jour l\'attribution de la tâche'),
      });
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      const comment = await addTaskComment(task.id, newComment);
      setComments(prevComments => [comment, ...prevComments]);
      setNewComment('');
      
      toast({
        title: t('tasks.commentAdded', 'Commentaire ajouté'),
        description: t('tasks.commentAddedDesc', 'Votre commentaire a été ajouté avec succès'),
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        variant: 'destructive',
        title: t('tasks.commentAddError', 'Erreur'),
        description: t('tasks.commentAddErrorDesc', 'Impossible d\'ajouter le commentaire'),
      });
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await deleteTaskComment(commentId);
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
      
      toast({
        title: t('tasks.commentDeleted', 'Commentaire supprimé'),
        description: t('tasks.commentDeletedDesc', 'Le commentaire a été supprimé avec succès'),
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        variant: 'destructive',
        title: t('tasks.commentDeleteError', 'Erreur'),
        description: t('tasks.commentDeleteErrorDesc', 'Impossible de supprimer le commentaire'),
      });
    }
  };

  // Détermine si l'utilisateur peut modifier l'attribution
  const canEditAssignment = currentUser?.role === 'ADMIN';

  return (
    <div className="space-y-6">
      {/* En-tête avec titre et actions */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">{task.title}</h1>
          <div className="flex items-center text-sm text-muted-foreground mt-1 space-x-4">
            <div className="flex items-center">
              <Tag className="h-4 w-4 mr-1" />
              <span>{task.clientName}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              <span>{formatDate(task.createdAt)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-2">
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-1" />
              {t('common.edit', 'Modifier')}
            </Button>
          )}
          {onDelete && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onDelete}
              className="text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t('common.delete', 'Supprimer')}
            </Button>
          )}
        </div>
      </div>

      {/* Informations principales et statut */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Colonne gauche: Informations principales */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('tasks.details', 'Détails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Statut */}
              <div>
                <Label>{t('tasks.status.label', 'Statut')}</Label>
                <Select 
                  defaultValue={task.status} 
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t('tasks.selectStatus', 'Sélectionner un statut')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TODO">
                      <div className="flex items-center">
                        <div className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></div>
                        {t('tasks.status.todo', 'À faire')}
                      </div>
                    </SelectItem>
                    <SelectItem value="IN_PROGRESS">
                      <div className="flex items-center">
                        <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
                        {t('tasks.status.inProgress', 'En cours')}
                      </div>
                    </SelectItem>
                    <SelectItem value="DONE">
                      <div className="flex items-center">
                        <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                        {t('tasks.status.done', 'Terminé')}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Client */}
              <div>
                <Label>{t('tasks.client', 'Client')}</Label>
                <Input className="mt-1" value={task.clientName} disabled />
              </div>

              {/* Date d'échéance */}
              <div>
                <Label>{t('tasks.dueDate', 'Date d\'échéance')}</Label>
                <div className="flex items-center mt-1 text-muted-foreground">
                  <Calendar className="h-4 w-4 mr-1" />
                  {formatDate(task.dueDate)}
                </div>
              </div>

              {/* Assigné à - modifié pour permettre l'édition */}
              <div>
                <div className="flex justify-between items-center">
                  <Label>{t('tasks.assignedTo', 'Assigné à')}</Label>
                  {!canEditAssignment && (
                    <Badge variant="outline" className="text-xs">
                      {t('task.adminOnly', 'Admin uniquement')}
                    </Badge>
                  )}
                  {canEditAssignment && !editingAssignment && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingAssignment(true)}
                      className="h-7 px-2 text-xs"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      {t('common.edit', 'Modifier')}
                    </Button>
                  )}
                </div>
                
                {editingAssignment ? (
                  <div className="mt-2 space-y-3">
                    <MultiSelect
                      options={users.map(user => ({
                        label: `${user.name} ${user.role === 'ADMIN' ? '(Admin)' : ''}`,
                        value: user.id.toString()
                      }))}
                      selected={assignedUsers.map(id => id.toString())}
                      onChange={values => setAssignedUsers(values.map(v => parseInt(v)))}
                      placeholder={t('tasks.selectUser', 'Sélectionner un utilisateur')}
                      className="w-full"
                    />
                    
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          setEditingAssignment(false);
                          setAssignedUsers(task.assignedToId ? [task.assignedToId] : []);
                        }}
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('common.cancel', 'Annuler')}
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleSaveAssignment}
                        disabled={savingAssignment}
                      >
                        {savingAssignment ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        {t('common.save', 'Enregistrer')}
                      </Button>
                    </div>
                  </div>
                ) : task.assignedTo ? (
                  <div className="flex items-center mt-2 space-x-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage 
                        src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(task.assignedTo.name)}`} 
                        alt={task.assignedTo.name} 
                      />
                      <AvatarFallback>{task.assignedTo.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{task.assignedTo.name}</p>
                      <p className="text-xs text-muted-foreground">{task.assignedTo.email}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('tasks.notAssigned', 'Non assigné')}
                  </p>
                )}
              </div>

              {/* Dates de création et mise à jour */}
              <div className="pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>{t('tasks.createdAt', 'Créé le')}:</span>
                    <span>{formatDate(task.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('tasks.updatedAt', 'Mis à jour le')}:</span>
                    <span>{formatDate(task.updatedAt)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emails associés */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                {t('tasks.associatedEmails', 'Emails associés')}
              </CardTitle>
              <CardDescription>
                {t('tasks.associatedEmailsDesc', 'Emails liés à cette tâche')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-4 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : emails.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {t('tasks.noEmails', 'Aucun email associé')}
                </div>
              ) : (
                <ul className="space-y-3">
                  {emails.map((email) => (
                    <li key={email.id} className="bg-accent/30 rounded-md p-2.5">
                      <div className="text-sm font-medium truncate mb-1">
                        {email.emailSubject}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(email.createdAt)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Colonne droite: Description et commentaires */}
        <div className="md:col-span-2">
          <div className="space-y-6">
            {/* Description */}
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle>{t('tasks.description', 'Description')}</CardTitle>
                <div>
                  {editingDescription ? (
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          setEditingDescription(false);
                          setDescription(task.description || '');
                        }}
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('common.cancel', 'Annuler')}
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleSaveDescription}
                        disabled={savingDescription}
                      >
                        {savingDescription ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        {t('common.save', 'Enregistrer')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingDescription(true)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {t('common.edit', 'Modifier')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingDescription ? (
                  <Textarea
                    className="min-h-[120px]"
                    placeholder={t('tasks.descriptionPlaceholder', 'Description de la tâche...')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                ) : (
                  <div className="prose prose-sm max-w-none">
                    {task.description ? (
                      <p className="whitespace-pre-wrap">{task.description}</p>
                    ) : (
                      <p className="text-muted-foreground italic">
                        {t('tasks.noDescription', 'Aucune description pour cette tâche')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Commentaires */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {t('tasks.comments', 'Commentaires')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Zone d'ajout de commentaire */}
                <div className="flex items-end gap-2">
                  <div className="flex-grow">
                    <Textarea
                      placeholder={t('tasks.addCommentPlaceholder', 'Ajouter un commentaire...')}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="min-h-[80px] resize-none"
                    />
                  </div>
                  <Button 
                    onClick={handleAddComment} 
                    disabled={!newComment.trim() || addingComment}
                    size="icon"
                  >
                    {addingComment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                
                {/* Liste des commentaires */}
                {loadingComments ? (
                  <div className="py-8 flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('tasks.noComments', 'Aucun commentaire')}
                  </div>
                ) : (
                  <ScrollArea className="h-[320px] pr-4">
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-muted/40 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-2">
                              <Avatar className="h-6 w-6">
                                <AvatarImage 
                                  src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(comment.author.name)}`} 
                                  alt={comment.author.name} 
                                />
                                <AvatarFallback>{comment.author.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">{comment.author.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(comment.createdAt)} {formatTime(comment.createdAt)}
                                </p>
                              </div>
                            </div>
                            {/* Bouton de suppression (visible seulement pour l'auteur ou les administrateurs) */}
                            {(currentUser?.id === comment.authorId || currentUser?.role === 'ADMIN') && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => handleDeleteComment(comment.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="text-sm whitespace-pre-wrap">{comment.content}</div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
